import { useState, useEffect } from 'react';
import { FileText, Info, Search, SortAsc, SortDesc } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { NewCaseForm } from './NewCase';
// import { FileViewer } from './FileViewer'; // Commented out as it's not used
import { EditCaseModal } from './modals/EditCaseModal';
import { AssignBikeModal } from './modals/AssignBikeModal';
import { UploadDocumentModal } from './modals/UploadDocumentModal';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';

// Import feature-specific components
import { NewCase } from './features/new-case';
import { UpdateCase } from './features/update-case';
import { UploadDocument } from './features/upload-document';
import { ViewDocuments } from './features/view-documents';
import { DeleteCase } from './features/delete-case';
import { AssignBike } from './features/assign-bike';
import { FinancialDetails } from './features/financial-details';

export function CaseManagement() {
  const [showNewCaseForm, setShowNewCaseForm] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState<string | null>(null);
  const [showEditModal, setShowEditModal] = useState<string | null>(null);
  const [showAssignBikeModal, setShowAssignBikeModal] = useState<string | null>(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState<string>('created_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [cases, setCases] = useState<any[]>([]);

  useEffect(() => {
    fetchCases();
  }, []);

  const fetchCases = async () => {
    try {
      const { data, error } = await supabase
        .from('cases')
        .select(`
          *,
          bike:bikes(
            id,
            model,
            rego_number,
            rate_a,
            rate_b,
            status,
            make
          )
        `)
        .neq('status', 'closed');

      if (error) throw error;
      setCases(sortCases(data || []));
    } catch (error) {
      console.error('Error fetching cases:', error);
    } finally {
      setLoading(false);
    }
  };

  const sortCases = (casesToSort: any[]) => {
    return [...casesToSort].sort((a, b) => {
      const direction = sortDirection === 'asc' ? 1 : -1;
      
      switch (sortField) {
        case 'created_at':
          return (new Date(a.created_at).getTime() - new Date(b.created_at).getTime()) * direction;
        case 'last_modified':
          return (new Date(a.last_modified).getTime() - new Date(b.last_modified).getTime()) * direction;
        case 'name':
          return (a.not_at_fault_name || '').localeCompare(b.not_at_fault_name || '') * direction;
        case 'afp_insurer':
          return (a.at_fault_insurance_company || '').localeCompare(b.at_fault_insurance_company || '') * direction;
        case 'status':
          return (a.status || '').localeCompare(b.status || '') * direction;
        default:
          return 0;
      }
    });
  };

  const toggleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
      setCases(prev => sortCases(prev));
    } else {
      setSortField(field);
      setSortDirection('asc');
      setCases(prev => sortCases(prev));
    }
  };

  const handleNewCase = async () => {
    try {
      const { data, error } = await supabase
        .from('cases')
        .insert([{ status: 'new_matter' }])
        .select()
        .single();

      if (error) throw error;
      setCases(prev => sortCases([data, ...prev]));
      // Open the edit modal for the new case
      setShowEditModal(data.id);
    } catch (error) {
      console.error('Error creating new case:', error);
    }
  };

  const handleDeleteCase = async (caseId: string) => {
    try {
      // First, check if the case has an assigned bike
      const caseToDelete = cases.find(c => c.id === caseId);
      if (caseToDelete?.bike_id) {
        // Update bike status back to available
        await supabase
          .from('bikes')
          .update({ status: 'available' })
          .eq('id', caseToDelete.bike_id);
      }

      // Delete all associated documents
      await supabase
        .from('documents')
        .delete()
        .eq('case_id', caseId);

      // Delete the case
      const { error } = await supabase
        .from('cases')
        .delete()
        .eq('id', caseId);

      if (error) throw error;

      // Update local state
      setCases(prev => prev.filter(c => c.id !== caseId));
      setShowDeleteConfirmation(null);
    } catch (error) {
      console.error('Error deleting case:', error);
      alert('Failed to delete case. Please try again.');
    }
  };

  const filteredCases = cases.filter(case_ => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    return (
      case_.reference_number?.toLowerCase().includes(searchLower) ||
      case_.not_at_fault_name?.toLowerCase().includes(searchLower) ||
      case_.not_at_fault_insurance_company?.toLowerCase().includes(searchLower) ||
      case_.claim_number?.toLowerCase().includes(searchLower)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#6563ff]"></div>
      </div>
    );
  }

  const SortButton = ({ field, label }: { field: string; label: string }) => (
    <button
      onClick={() => toggleSort(field)}
      className="flex items-center space-x-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
    >
      <span>{label}</span>
      {sortField === field && (
        sortDirection === 'asc' ? 
          <SortAsc className="h-4 w-4 text-[#6563ff]" /> : 
          <SortDesc className="h-4 w-4 text-[#6563ff]" />
      )}
    </button>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
        <h1 className="text-3xl font-bold text-gray-900">CASE MANAGEMENT</h1>
        <div className="flex items-center space-x-4">
          <div className="relative">
            <input
              type="text"
              placeholder="Search cases..."
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
          </div>
          <NewCase onSuccess={handleNewCase} />
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-lg p-4 border border-gray-100">
        <div className="flex flex-wrap gap-6 mb-4">
          <SortButton field="created_at" label="Created Date" />
          <SortButton field="last_modified" label="Last Modified" />
          <SortButton field="name" label="Client Name" />
          <SortButton field="afp_insurer" label="At Fault Insurer" />
          <SortButton field="status" label="Status" />
        </div>
      </div>

      <div className="space-y-6">
        {filteredCases.map(case_ => (
          <div 
            key={case_.id}
            className="bg-white rounded-xl shadow-lg overflow-hidden border-l-4 transition-all hover:scale-[1.02] hover:shadow-xl"
            style={{
              borderLeftColor: getStatusColor(case_.status).split(' ')[0].replace('bg-', '#'),
              background: 'linear-gradient(to right, rgba(255,255,255,0.5), white)'
            }}
          >
            <div className="p-6">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-4">
                <div className="flex items-center space-x-3">
                  <h3 className="text-lg font-semibold text-gray-800">
                    {case_.case_number}
                  </h3>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(case_.status)}`}>
                    {formatStatus(case_.status)}
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  <UpdateCase 
                    caseId={case_.id} 
                    onSuccess={() => setShowEditModal(case_.id)} 
                  />
                  
                  <UploadDocument 
                    caseId={case_.id} 
                    onSuccess={() => setShowUploadModal(case_.id)} 
                  />
                  
                  <ViewDocuments 
                    caseId={case_.id} 
                  />
                  
                  {/* Use React Router Link with state to ensure proper navigation */}
                  <Link 
                    to={`/client-data/${case_.id}`}
                    className="flex items-center gap-1 text-blue-600 hover:text-blue-800 px-2 py-1 rounded-md hover:bg-blue-50"
                    data-testid="edit-client-data-btn"
                    state={{ fromCasesList: true }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                    <span>Client Data</span>
                  </Link>
                  
                  <DeleteCase 
                    caseId={case_.id} 
                    onSuccess={() => fetchCases()} 
                  />
                  
                  {!case_.bike_id && (
                    <AssignBike 
                      caseId={case_.id} 
                      onSuccess={() => setShowAssignBikeModal(case_.id)} 
                    />
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-1">Client Details</h4>
                  <p className="text-gray-900 font-medium">{case_.not_at_fault_name || 'Unnamed Client'}</p>
                  <p className="text-sm text-gray-600">Ref: {case_.reference_number}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-1">Insurance Details</h4>
                  <p className="text-gray-900">{case_.not_at_fault_insurance_company || 'Not specified'}</p>
                  <p className="text-sm text-gray-600">Claim: {case_.claim_number || 'Not specified'}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Financial Details */}
                <FinancialDetails 
                  caseId={case_.id}
                  invoicedAmount={Number(case_.invoiced_amount) || 0}
                  reserveAmount={Number(case_.reserve_amount) || 0}
                  settlementAmount={Number(case_.settlement_amount) || 0}
                  paidAmount={Number(case_.paid_amount) || 0}
                  onUpdate={() => fetchCases()}
                />
                {/* Bike Details */}
                {case_.bike ? (
                  <div className="bg-[#6563ff]/5 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-[#6563ff] mb-3 flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      Bike Details
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-gray-500">Bike</p>
                        <p className="text-sm font-semibold text-gray-900">
                          {case_.bike.make} {case_.bike.model}
                        </p>
                        <p className="text-xs text-gray-600">{case_.bike.rego_number}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Daily Rates</p>
                        <p className="text-sm text-gray-900">Rate A: ${case_.bike.rate_a?.toFixed(2) || '0.00'}</p>
                        <p className="text-sm text-gray-900">Rate B: ${case_.bike.rate_b?.toFixed(2) || '0.00'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Days Rented</p>
                        <p className="text-sm font-semibold text-gray-900">{case_.total_days || 0} days</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Current Total</p>
                        <p className="text-sm text-gray-900">
                          Rate A: ${((case_.total_days || 0) * (case_.bike.rate_a || 0)).toFixed(2)}
                        </p>
                        <p className="text-sm text-gray-900">
                          Rate B: ${((case_.total_days || 0) * (case_.bike.rate_b || 0)).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-500">No bike assigned</p>
                  </div>
                )}
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-gray-500">
                <span className="flex items-center">
                  <Info className="h-3 w-3 mr-1" />
                  Modified: {format(new Date(case_.last_modified), 'dd MMM yyyy HH:mm')}
                </span>
                <span className="flex items-center">
                  <Info className="h-3 w-3 mr-1" />
                  Created: {format(new Date(case_.created_at), 'dd MMM yyyy')}
                </span>
              </div>
            </div>
          </div>
        ))}

        {filteredCases.length === 0 && (
          <div className="text-center py-12 bg-white rounded-xl shadow-lg">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No cases found</h3>
            <p className="text-gray-500">Create your first case to get started.</p>
          </div>
        )}
      </div>

      {showNewCaseForm && (
        <NewCaseForm
          onClose={() => setShowNewCaseForm(false)}
          onCaseCreated={() => {
            setShowNewCaseForm(false);
            fetchCases();
          }}
        />
      )}

      {showUploadModal && (
        <UploadDocumentModal
          caseId={showUploadModal as string}
          onClose={() => setShowUploadModal(null)}
          onDocumentUploaded={() => setShowUploadModal(null)}
        />
      )}

      {showEditModal && (
        <EditCaseModal
          caseId={showEditModal as string}
          onClose={() => setShowEditModal(null)}
          onCaseUpdated={() => {
            setShowEditModal(null);
            fetchCases();
          }}
        />
      )}

      {showAssignBikeModal && (
        <AssignBikeModal
          caseId={showAssignBikeModal as string}
          onClose={() => setShowAssignBikeModal(null)}
          onBikeAssigned={() => {
            setShowAssignBikeModal(null);
            fetchCases();
          }}
        />
      )}

      {showDeleteConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Confirm Delete</h2>
            <p className="text-gray-700 mb-6">
              Are you sure you want to delete this case? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setShowDeleteConfirmation(null)}
                className="px-4 py-2 text-gray-700 hover:text-gray-900"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteCase(showDeleteConfirmation as string)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function getStatusColor(status: string): string {
  const colors: Record<string, string> = {
    new_matter: 'bg-gray-100 text-gray-800',
    customer_contacted: 'bg-blue-100 text-blue-800',
    awaiting_approval: 'bg-yellow-100 text-yellow-800',
    bike_delivered: 'bg-green-100 text-green-800',
    bike_returned: 'bg-purple-100 text-purple-800',
    demands_sent: 'bg-indigo-100 text-indigo-800',
    awaiting_settlement: 'bg-orange-100 text-orange-800',
    settlement_agreed: 'bg-emerald-100 text-emerald-800',
    paid: 'bg-teal-100 text-teal-800',
    closed: 'bg-gray-100 text-gray-800'
  };
  return colors[status] || colors.new_matter;
}

function formatStatus(status: string): string {
  return status
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}